import { api } from "./api";
import type {
  AddonToggleRequest,
  AddonToggleResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  EnterpriseContactRequest,
  EnterpriseContactResponse,
  PortalSessionResponse,
  SetupIntentResponse,
  StartTrialRequest,
  StartTrialResponse,
  SubscriptionState,
} from "@/types";

// Source: backend/app/api/v1/billing.py + ENG-BILLING-STRIPE.
//
// Every endpoint relies on the shared axios instance's
// ``withCredentials: true`` config so the ``aiaso_session`` cookie
// flows transparently. The webhook endpoint (POST /webhooks/stripe)
// is server-only — Stripe-issued — and is NOT included here.

export async function fetchSubscriptionState(): Promise<SubscriptionState> {
  const { data } = await api.get<SubscriptionState>(
    "/api/v1/billing/subscription",
  );
  return data;
}

export async function createCheckoutSession(
  payload: CheckoutSessionRequest,
): Promise<CheckoutSessionResponse> {
  const { data } = await api.post<CheckoutSessionResponse>(
    "/api/v1/billing/checkout-session",
    payload,
  );
  return data;
}

export async function createPortalSession(): Promise<PortalSessionResponse> {
  const { data } = await api.post<PortalSessionResponse>(
    "/api/v1/billing/portal-session",
  );
  return data;
}

export async function toggleComplianceAddon(
  payload: AddonToggleRequest,
): Promise<AddonToggleResponse> {
  const { data } = await api.post<AddonToggleResponse>(
    "/api/v1/billing/addons/compliance",
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// /start-trial (Onboarding Step 1 — Stripe Elements card-entry flow)
// ---------------------------------------------------------------------------
//
// Operator picks Starter / Pro tier + monthly/annual cadence; Stripe.js
// creates a PaymentMethod (``pm_…``) via ``confirmCardSetup`` client-side;
// the backend creates the Stripe Customer + attaches the PaymentMethod +
// creates the Subscription with ``trial_period_days=7``, atomically
// flipping ``Workspace.signup_state`` to ``'trial_active'``. HTTP 502
// surfaces Stripe API failures; HTTP 422 surfaces price-not-configured /
// payment-method-not-attached / schema-validation failures; HTTP 409
// surfaces a signup-state-conflict (workspace already past
// ``signup_complete`` / ``trial_pending_sales``).

export async function startTrial(
  payload: StartTrialRequest,
): Promise<StartTrialResponse> {
  const { data } = await api.post<StartTrialResponse>(
    "/api/v1/billing/start-trial",
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// /enterprise-contact (Onboarding Step 1 — Enterprise sales-lead flow)
// ---------------------------------------------------------------------------
//
// Operator clicks ``[Talk to us]`` on the Enterprise card, fills the
// 6-field contact form, the backend records the lead via
// ``enterprise_lead_created`` OutboxEvent + best-effort Slack + email
// notifications + flips ``Workspace.signup_state`` to
// ``'trial_pending_sales'``. HTTP 409 surfaces a signup-state-conflict
// (NOT idempotent on re-submit — the second submit's payload would
// otherwise be silently dropped; route through CRM directly to re-engage).

export async function submitEnterpriseContact(
  payload: EnterpriseContactRequest,
): Promise<EnterpriseContactResponse> {
  const { data } = await api.post<EnterpriseContactResponse>(
    "/api/v1/billing/enterprise-contact",
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// /setup-intent (Onboarding Step 1 — SetupIntent client_secret issuance)
// ---------------------------------------------------------------------------
//
// The backend creates a Stripe Customer (if not already attached to the
// operator) + issues a SetupIntent attached to that Customer. The
// frontend passes ``client_secret`` into ``stripe.confirmCardSetup`` to
// create the PaymentMethod + handle 3DS / SCA inline as a modal-over-
// modal, then hands the confirmed payment_method_id to
// ``startTrial`` for Subscription creation.

export async function createBillingSetupIntent(): Promise<SetupIntentResponse> {
  const { data } = await api.post<SetupIntentResponse>(
    "/api/v1/billing/setup-intent",
  );
  return data;
}
